// Item data: weapons, weapon mods, equipment, mutations, synergies, major
// upgrades and the affix pools rolled onto advanced/prototype items.
// Phaser-free (imported by pure loot logic, core/run and unit tests).
//
// Balance baseline (sector-1 enemies have 40-150 HP):
//   pulse_carbine ~110 DPS (≈2.5 s of sustained fire before overheating)
//   scatterforge  ~150 DPS point blank      arc_lance     ~110 DPS + chains
//   rail_driver   ~170 DPS with full charges (cycle = chargeTime + 1/fireRate)
//   micro_rockets ~130 DPS in bursts         gravity_launcher ~80 DPS + crowd control
// Higher rarity adds identity affixes, never just raw damage (DESIGN §6).

import { TEX } from '../art/keys';
import { COLORS } from '../config/palette';
import type {
  EquipDef,
  EquipId,
  EquipSlot,
  ItemKind,
  MajorUpgradeDef,
  MajorUpgradeId,
  MechId,
  ModDef,
  ModId,
  MutationDef,
  MutationId,
  StatKey,
  SynergyDef,
  SynergyId,
  Tag,
  UnlockId,
  WeaponDef,
  WeaponId,
} from '../core/types';

// ───────────────────────────── Extended def types ─────────────────────────────
// The shared def types have no card identity line (and MutationDef has no
// unlock id). These intersections stay assignable to the shared types.

export type EquipData = EquipDef & { short: string };
export type ModData = ModDef & { short: string };
export type MutationData = MutationDef & { short: string; unlock?: UnlockId };
export type UpgradeData = MajorUpgradeDef & { short: string };

// ───────────────────────────── Weapons ─────────────────────────────

export const WEAPONS: Record<WeaponId, WeaponDef> = {
  pulse_carbine: {
    id: 'pulse_carbine',
    name: 'Pulse Carbine',
    short: 'Reliable automatic',
    desc: 'Hold to fire 10 pulse rounds per second. Roughly 2.5 s of sustained fire before it overheats.',
    tags: ['kinetic'],
    behavior: 'auto',
    holdStyle: 'rifle',
    damage: 11,
    fireRate: 10,
    projectileSpeed: 1900,
    spread: 0.035,
    pellets: 1,
    range: 1250,
    pierce: 0,
    knockback: 70,
    heatPerShot: 0.04,
    coolRate: 0.6,
    coolDelay: 0.22,
    overheatTime: 1.1,
    projectile: { texture: TEX.projPulse, radius: 5, scale: 1, tint: 0xbff8ff },
    muzzleColor: 0x9ff6ff,
    sfx: 'shoot_pulse',
    recoil: 0.22,
    shake: 0.0012,
    weight: 0.7,
  },
  scatterforge: {
    id: 'scatterforge',
    name: 'Scatterforge',
    short: 'Point-blank shotgun',
    desc: 'Blasts 7 heavy pellets in a tight cone with brutal knockback. Lethal inside 250 px, spent by 520 px.',
    tags: ['kinetic'],
    behavior: 'spread',
    holdStyle: 'heavy',
    damage: 13,
    fireRate: 1.65,
    projectileSpeed: 1600,
    spread: 0.4,
    pellets: 7,
    range: 520,
    pierce: 0,
    knockback: 560,
    heatPerShot: 0.17,
    coolRate: 0.62,
    coolDelay: 0.35,
    overheatTime: 1.3,
    projectile: { texture: TEX.projPellet, radius: 5, scale: 1, tint: 0xffe0a0 },
    muzzleColor: 0xffc457,
    sfx: 'shoot_scatter',
    recoil: 0.85,
    shake: 0.0055,
    weight: 1,
  },
  arc_lance: {
    id: 'arc_lance',
    name: 'Arc Lance',
    short: 'Chain lightning',
    desc: 'A crackling arc locks onto the nearest enemy in front of you and leaps to 2 more within 220 px.',
    tags: ['electric', 'chain'],
    behavior: 'beam_chain',
    holdStyle: 'compact',
    damage: 13,
    fireRate: 8.5,
    projectileSpeed: 0,
    spread: 0.6,
    pellets: 1,
    range: 400,
    pierce: 0,
    knockback: 40,
    heatPerShot: 0.036,
    coolRate: 0.65,
    coolDelay: 0.2,
    overheatTime: 1.2,
    chain: { count: 2, range: 220, falloff: 0.75 },
    projectile: { texture: TEX.projPulse, radius: 4, scale: 0.8, tint: COLORS.electric },
    muzzleColor: COLORS.electric,
    sfx: 'shoot_arc',
    recoil: 0.08,
    shake: 0.0005,
    weight: 1,
  },
  rail_driver: {
    id: 'rail_driver',
    name: 'Rail Driver',
    short: 'Piercing charge shot',
    desc: 'Hold to charge for 0.7 s, release a hypersonic slug that pierces 4 enemies. A full charge hits for 155.',
    tags: ['precision', 'kinetic'],
    behavior: 'charge',
    holdStyle: 'rifle',
    damage: 155,
    fireRate: 5,
    projectileSpeed: 4200,
    spread: 0,
    pellets: 1,
    range: 1900,
    pierce: 4,
    knockback: 380,
    heatPerShot: 0.34,
    coolRate: 0.55,
    coolDelay: 0.3,
    overheatTime: 1.4,
    chargeTime: 0.7,
    projectile: { texture: TEX.projRail, radius: 6, scale: 1, tint: 0xe2f4ff },
    muzzleColor: 0xd6f0ff,
    sfx: 'shoot_rail',
    recoil: 1,
    shake: 0.0075,
    weight: 0.9,
  },
  micro_rockets: {
    id: 'micro_rockets',
    name: 'Micro-Rocket Rack',
    short: 'Homing salvo',
    desc: 'Each pull launches 4 tracking micro-missiles that burst in 72 px blasts. Fire, then keep moving.',
    tags: ['explosive', 'missile'],
    behavior: 'missile_burst',
    holdStyle: 'heavy',
    damage: 20,
    fireRate: 1.1,
    projectileSpeed: 950,
    spread: 0.55,
    pellets: 1,
    range: 1100,
    pierce: 0,
    knockback: 160,
    heatPerShot: 0.22,
    coolRate: 0.5,
    coolDelay: 0.4,
    overheatTime: 1.4,
    burst: { count: 4, interval: 0.07 },
    explode: { radius: 72, damageMult: 0.5 },
    homing: { turnRate: 7.5, range: 560, delay: 0.12 },
    projectile: { texture: TEX.projRocket, radius: 6, scale: 1, tint: 0xffd2a0 },
    muzzleColor: COLORS.missile,
    sfx: 'shoot_rocket',
    recoil: 0.45,
    shake: 0.003,
    weight: 1,
  },
  gravity_launcher: {
    id: 'gravity_launcher',
    name: 'Gravity Launcher',
    short: 'Crowd-control well',
    desc: 'Lobs a singularity grenade that opens a 160 px gravity well for 1.6 s, dragging enemies together before it implodes.',
    tags: ['gravity', 'explosive'],
    behavior: 'lobber',
    holdStyle: 'heavy',
    damage: 30,
    fireRate: 0.95,
    projectileSpeed: 1050,
    spread: 0,
    pellets: 1,
    range: 1000,
    pierce: 0,
    knockback: 120,
    heatPerShot: 0.3,
    coolRate: 0.5,
    coolDelay: 0.5,
    overheatTime: 1.5,
    explode: { radius: 100, damageMult: 0.5 },
    field: { radius: 160, duration: 1.6, strength: 1500, implodeDamageMult: 1.2 },
    gravity: 1500,
    projectile: { texture: TEX.projGravity, radius: 9, scale: 1, tint: 0xd0b4ff },
    muzzleColor: COLORS.gravity,
    sfx: 'shoot_gravity',
    recoil: 0.6,
    shake: 0.004,
    weight: 0.85,
    unlock: 'weapon:gravity_launcher',
  },
};

// ───────────────────────────── Weapon mods ─────────────────────────────

export const MODS: Record<ModId, ModData> = {
  ionized: {
    id: 'ionized',
    name: 'Ionizer',
    short: 'Shock rounds',
    desc: 'Every hit adds a shock stack (2.5 s). Three stacks stun for 0.6 s; shocked targets take +15% electric damage.',
    tags: ['electric'],
    weapon: {
      addTags: ['electric'],
      addStatuses: [{ id: 'shock', stacks: 1, duration: 2.5 }],
      damageMult: 0.9,
    },
    tradeoff: '-10% damage',
    weight: 1,
  },
  incendiary: {
    id: 'incendiary',
    name: 'Incendiary Core',
    short: 'Burning rounds',
    desc: 'Hits ignite: 1 burn stack (6 dps for 3 s), stacking to 5. Five stacks overheat the target.',
    tags: ['burn'],
    weapon: {
      addTags: ['burn'],
      addStatuses: [{ id: 'burn', stacks: 1, duration: 3, potency: 6 }],
      heatMult: 1.15,
    },
    tradeoff: '+15% heat per shot',
    weight: 1,
  },
  fragmenting: {
    id: 'fragmenting',
    name: 'Fragmenting Shells',
    short: 'Kills detonate',
    desc: 'Kills with this weapon blow the target apart: a 95 px blast for 125% of the killing hit.',
    tags: ['explosive'],
    weapon: {
      addTags: ['explosive'],
      explodeOnKill: { radius: 95, damageMult: 1.25 },
      projectileSpeedMult: 0.85,
    },
    tradeoff: '-15% projectile speed',
    weight: 1,
  },
  overclocked: {
    id: 'overclocked',
    name: 'Overclock Chip',
    short: 'Faster trigger',
    desc: '+35% fire rate. The barrel runs hot: +30% heat per shot.',
    tags: ['heat'],
    weapon: { fireRateMult: 1.35, heatMult: 1.3 },
    tradeoff: '+30% heat per shot',
    weight: 1,
  },
  cryo_coil: {
    id: 'cryo_coil',
    name: 'Cryo Coil',
    short: 'Sustained fire',
    desc: 'A liquid-nitrogen jacket: the weapon cools 60% faster between bursts.',
    tags: ['heat'],
    weapon: { coolMult: 1.6, damageMult: 0.9 },
    tradeoff: '-10% damage',
    weight: 1,
  },
  split_shot: {
    id: 'split_shot',
    name: 'Split Chamber',
    short: 'Triple fan',
    desc: 'ALT FIRE: every shot splits into 3 projectiles fanned around your aim.',
    tags: ['kinetic'],
    weapon: { extraProjectiles: 2, damageMult: 0.65, spreadAdd: 0.22 },
    tradeoff: '-35% damage per projectile',
    altMode: true,
    weight: 0.85,
  },
  burst_fire: {
    id: 'burst_fire',
    name: 'Burst Trigger',
    short: 'Three-round bursts',
    desc: 'ALT FIRE: each pull fires a tight 3-round burst. Bursts cycle at 42% of the normal rate (+26% rounds overall).',
    tags: ['precision'],
    weapon: { burst: { count: 3, interval: 0.055 }, fireRateMult: 0.42, heatMult: 0.85 },
    tradeoff: 'Pauses between bursts',
    altMode: true,
    weight: 0.85,
  },
  heavy_rounds: {
    id: 'heavy_rounds',
    name: 'Heavy Rounds',
    short: 'Dense slugs',
    desc: 'Tungsten-cored ammunition: +40% damage and +40% knockback per hit.',
    tags: ['kinetic'],
    weapon: { damageMult: 1.4, knockbackMult: 1.4, fireRateMult: 0.75 },
    tradeoff: '-25% fire rate',
    weight: 1,
  },
};

// ───────────────────────────── Equipment ─────────────────────────────

export const EQUIPMENT: Record<EquipId, EquipData> = {
  // Chest
  reactive_plating: {
    id: 'reactive_plating',
    slot: 'chest',
    name: 'Reactive Plating',
    short: 'Retaliation armor',
    desc: '+30 max shield. Getting hit detonates a plate: a 150 px blast for 35 damage (1.5 s cooldown).',
    tags: ['shield', 'explosive'],
    stats: [
      { stat: 'maxShield', op: 'add', value: 30 },
      { stat: 'moveSpeed', op: 'mul', value: 0.94 },
    ],
    effects: [
      { trigger: 'onDamaged', cooldown: 1.5, action: { type: 'explode', radius: 150, damage: 35, tags: ['explosive'] } },
    ],
    tradeoff: '-6% move speed',
    weight: 1,
  },
  ablative_shell: {
    id: 'ablative_shell',
    slot: 'chest',
    name: 'Ablative Shell',
    short: 'Layered hull',
    desc: '+25 max HP and 15% armor. When your shield breaks, armor rises another 20% for 3 s.',
    tags: ['shield', 'kinetic'],
    stats: [
      { stat: 'maxHp', op: 'add', value: 25 },
      { stat: 'armor', op: 'add', value: 0.15 },
      { stat: 'knockbackResist', op: 'add', value: 0.25 },
      { stat: 'dashCooldown', op: 'mul', value: 1.15 },
    ],
    effects: [{ trigger: 'onShieldBreak', action: { type: 'buff', stat: 'armor', op: 'add', value: 0.2, duration: 3 } }],
    tradeoff: '+15% dash cooldown',
    weight: 1,
  },
  capacitor_core: {
    id: 'capacitor_core',
    slot: 'chest',
    name: 'Capacitor Core',
    short: 'Static discharge',
    desc: 'Your shield doubles as a capacitor: taking damage arcs lightning into 3 nearby enemies for 24 (1 s cooldown).',
    tags: ['electric', 'shield'],
    stats: [
      { stat: 'maxShield', op: 'add', value: 20 },
      { stat: 'shieldDelay', op: 'mul', value: 0.8 },
      { stat: 'maxHp', op: 'add', value: -10 },
    ],
    effects: [{ trigger: 'onDamaged', cooldown: 1, action: { type: 'chain', count: 3, range: 300, damage: 24 } }],
    tradeoff: '-10 max HP',
    weight: 1,
  },
  // Shoulders
  missile_pods: {
    id: 'missile_pods',
    slot: 'shoulders',
    name: 'Missile Pods',
    short: 'Autonomous salvo',
    desc: 'Shoulder pods launch 2 homing micro-missiles (18 damage each) every 2.5 s of combat.',
    tags: ['explosive', 'missile'],
    stats: [{ stat: 'heatCapacity', op: 'mul', value: 0.9 }],
    effects: [{ trigger: 'tick', interval: 2.5, action: { type: 'missiles', count: 2, damage: 18 } }],
    tradeoff: '-10% heat capacity',
    weight: 1,
  },
  storm_capacitor: {
    id: 'storm_capacitor',
    slot: 'shoulders',
    name: 'Storm Capacitor',
    short: 'Chain amplifier',
    desc: 'All chain lightning jumps to 1 extra target and deals +20% damage. Electric hits have a 12% chance to fork.',
    tags: ['electric', 'chain'],
    stats: [
      { stat: 'chainBonus', op: 'add', value: 1 },
      { stat: 'chainDamage', op: 'mul', value: 1.2 },
      { stat: 'maxShield', op: 'add', value: -10 },
    ],
    effects: [
      {
        trigger: 'onHit',
        requiresTag: 'electric',
        chance: 0.12,
        cooldown: 0.25,
        action: { type: 'chain', count: 2, range: 240, damage: 14 },
      },
    ],
    tradeoff: '-10 max shield',
    weight: 1,
  },
  aegis_pauldrons: {
    id: 'aegis_pauldrons',
    slot: 'shoulders',
    name: 'Aegis Pauldrons',
    short: 'Fortified shield',
    desc: '+35 max shield that recharges 25% faster. A shield break halves incoming damage for 1.5 s.',
    tags: ['shield'],
    stats: [
      { stat: 'maxShield', op: 'add', value: 35 },
      { stat: 'shieldRegen', op: 'mul', value: 1.25 },
      { stat: 'damage', op: 'mul', value: 0.93 },
    ],
    effects: [
      { trigger: 'onShieldBreak', action: { type: 'buff', stat: 'damageTaken', op: 'mul', value: 0.5, duration: 1.5 } },
    ],
    tradeoff: '-7% damage dealt',
    weight: 1,
  },
  // Back
  overdrive_reactor: {
    id: 'overdrive_reactor',
    slot: 'back',
    name: 'Overdrive Reactor',
    short: 'Redline reactor',
    desc: '+20% fire rate and +15% ultimate charge. Venting after an overheat surges +30% damage for 3 s.',
    tags: ['heat'],
    stats: [
      { stat: 'fireRate', op: 'mul', value: 1.2 },
      { stat: 'ultimateCharge', op: 'mul', value: 1.15 },
      { stat: 'heatCapacity', op: 'mul', value: 0.85 },
    ],
    effects: [{ trigger: 'onVentEnd', action: { type: 'buff', stat: 'damage', op: 'mul', value: 1.3, duration: 3 } }],
    tradeoff: '-15% heat capacity',
    weight: 1,
  },
  tempest_reactor: {
    id: 'tempest_reactor',
    slot: 'back',
    name: 'Tempest Reactor',
    short: 'Storm core',
    desc: 'Chain lightning deals +30% damage. Every signature use unleashes a 3-target lightning storm (35 damage).',
    tags: ['electric', 'chain'],
    stats: [
      { stat: 'chainDamage', op: 'mul', value: 1.3 },
      { stat: 'cooling', op: 'mul', value: 0.85 },
    ],
    effects: [{ trigger: 'onSignature', action: { type: 'chain', count: 3, range: 320, damage: 35 } }],
    tradeoff: '-15% weapon cooling',
    weight: 1,
  },
  heat_sink_array: {
    id: 'heat_sink_array',
    slot: 'back',
    name: 'Heat Sink Array',
    short: 'Thermal exhaust',
    desc: '+25% heat capacity and +20% cooling. Overheating vents scorching exhaust: 2 burn stacks within 180 px.',
    tags: ['heat', 'burn'],
    stats: [
      { stat: 'heatCapacity', op: 'mul', value: 1.25 },
      { stat: 'cooling', op: 'mul', value: 1.2 },
      { stat: 'maxShield', op: 'add', value: -10 },
    ],
    effects: [
      {
        trigger: 'onOverheat',
        action: { type: 'status', status: { id: 'burn', stacks: 2, duration: 3, potency: 8 }, radius: 180 },
      },
    ],
    tradeoff: '-10 max shield',
    weight: 1,
  },
  jump_jets: {
    id: 'jump_jets',
    slot: 'back',
    name: 'Jump Jets',
    short: 'Vertical thrust',
    desc: '+1 air jump and +8% jump height. Jumps kick out a 120 px shockwave (14 damage, 0.8 s cooldown).',
    tags: ['dash'],
    stats: [
      { stat: 'airJumps', op: 'add', value: 1 },
      { stat: 'jumpPower', op: 'mul', value: 1.08 },
      { stat: 'dashPower', op: 'mul', value: 0.9 },
    ],
    effects: [{ trigger: 'onJump', cooldown: 0.8, action: { type: 'shockwave', radius: 120, damage: 14, knockback: 520 } }],
    tradeoff: '-10% dash distance',
    weight: 0.85,
    unlock: 'equip:jump_jets',
  },
  // Legs
  vector_boosters: {
    id: 'vector_boosters',
    slot: 'legs',
    name: 'Vector Boosters',
    short: 'Lightning stride',
    desc: '-20% dash cooldown, +8% move speed. Dashes leave a crackling trail: 30 dps and a shock stack for 1.6 s.',
    tags: ['dash', 'electric'],
    stats: [
      { stat: 'dashCooldown', op: 'mul', value: 0.8 },
      { stat: 'moveSpeed', op: 'mul', value: 1.08 },
      { stat: 'maxHp', op: 'add', value: -10 },
    ],
    effects: [
      {
        trigger: 'onDashEnd',
        action: { type: 'trail', kind: 'electric', dps: 30, duration: 1.6, status: { id: 'shock', stacks: 1, duration: 2 } },
      },
    ],
    tradeoff: '-10 max HP',
    dashTrail: 'electric',
    weight: 1,
  },
  cinder_treads: {
    id: 'cinder_treads',
    slot: 'legs',
    name: 'Cinder Treads',
    short: 'Scorched earth',
    desc: 'Dashes leave burning ground for 2.2 s: 24 dps plus a burn stack. +5% move speed.',
    tags: ['dash', 'burn'],
    stats: [
      { stat: 'moveSpeed', op: 'mul', value: 1.05 },
      { stat: 'cooling', op: 'mul', value: 0.9 },
    ],
    effects: [
      {
        trigger: 'onDashEnd',
        action: {
          type: 'trail',
          kind: 'burn',
          dps: 24,
          duration: 2.2,
          status: { id: 'burn', stacks: 1, duration: 3, potency: 6 },
        },
      },
    ],
    tradeoff: '-10% weapon cooling',
    dashTrail: 'burn',
    weight: 1,
  },
  servo_striders: {
    id: 'servo_striders',
    slot: 'legs',
    name: 'Servo Striders',
    short: 'Pure speed',
    desc: '+12% move speed, +20% dash distance, -10% dash cooldown. Every kill refunds 0.3 s of dash cooldown.',
    tags: ['dash', 'kinetic'],
    stats: [
      { stat: 'moveSpeed', op: 'mul', value: 1.12 },
      { stat: 'dashPower', op: 'mul', value: 1.2 },
      { stat: 'dashCooldown', op: 'mul', value: 0.9 },
      { stat: 'damageTaken', op: 'mul', value: 1.08 },
    ],
    effects: [{ trigger: 'onKill', action: { type: 'cooldown', ability: 'dash', seconds: 0.3 } }],
    tradeoff: '+8% damage taken',
    dashTrail: 'plasma',
    weight: 1,
  },
  anchor_greaves: {
    id: 'anchor_greaves',
    slot: 'legs',
    name: 'Anchor Greaves',
    short: 'Immovable stance',
    desc: '+50% knockback resistance, 10% armor, +20 shield. Heavy landings slam a 200 px shockwave (40 damage).',
    tags: ['shield', 'kinetic'],
    stats: [
      { stat: 'knockbackResist', op: 'add', value: 0.5 },
      { stat: 'armor', op: 'add', value: 0.1 },
      { stat: 'maxShield', op: 'add', value: 20 },
      { stat: 'moveSpeed', op: 'mul', value: 0.88 },
    ],
    effects: [{ trigger: 'onLand', cooldown: 0.5, action: { type: 'shockwave', radius: 200, damage: 40, knockback: 900 } }],
    tradeoff: '-12% move speed',
    weight: 0.85,
    unlock: 'equip:anchor_greaves',
  },
};

// ───────────────────────────── Mutations ─────────────────────────────

export const MUTATIONS: Record<MutationId, MutationData> = {
  chain_reactor: {
    id: 'chain_reactor',
    name: 'Chain Reactor',
    short: 'Lightning ignites',
    desc: 'Your lightning runs white-hot: every chain-lightning hit applies a burn stack.',
    drawback: 'Chain lightning deals 15% less damage',
    tags: ['electric', 'burn'],
    stats: [{ stat: 'chainDamage', op: 'mul', value: 0.85 }],
    effects: [],
    flags: ['CHAIN_REACTOR'],
    aura: COLORS.burn,
    weight: 1,
  },
  glass_core: {
    id: 'glass_core',
    name: 'Glass Core',
    short: 'All-in damage',
    desc: 'Strip out the safeties: +40% damage dealt.',
    drawback: '+30% damage taken and -20% max HP',
    tags: [],
    stats: [
      { stat: 'damage', op: 'mul', value: 1.4 },
      { stat: 'damageTaken', op: 'mul', value: 1.3 },
      { stat: 'maxHp', op: 'mul', value: 0.8 },
    ],
    effects: [],
    aura: 0xe8f6ff,
    weight: 0.9,
  },
  meltdown_engine: {
    id: 'meltdown_engine',
    name: 'Meltdown Engine',
    short: 'Fire through overheat',
    desc: 'Overheating no longer locks your weapon, and shots fired while overheated deal +50% damage.',
    drawback: 'Every shot fired while overheated costs 2 HP',
    tags: ['heat'],
    stats: [],
    effects: [],
    flags: ['MELTDOWN'],
    aura: 0xff4a1a,
    weight: 0.9,
    unlock: 'mutation:meltdown_engine',
  },
  phase_capacitor: {
    id: 'phase_capacitor',
    name: 'Phase Capacitor',
    short: 'Charged dodges',
    desc: 'Dash invulnerability lasts 0.12 s longer, and every enemy you dash through takes 2 shock stacks.',
    drawback: '+20% dash cooldown',
    tags: ['dash', 'electric'],
    stats: [{ stat: 'dashCooldown', op: 'mul', value: 1.2 }],
    effects: [],
    flags: ['PHASE_CAPACITOR'],
    aura: COLORS.electric,
    weight: 1,
  },
  volatile_payload: {
    id: 'volatile_payload',
    name: 'Volatile Payload',
    short: 'Kills detonate',
    desc: 'Every kill explodes for 25% of the victim\'s max HP in a 90 px blast.',
    drawback: 'Your explosions can hit you for 5 damage',
    tags: ['explosive'],
    stats: [],
    effects: [],
    flags: ['VOLATILE_PAYLOAD'],
    aura: COLORS.explosive,
    weight: 1,
  },
  feedback_loop: {
    id: 'feedback_loop',
    name: 'Feedback Loop',
    short: 'Blood-fed signature',
    desc: 'Route the reactor through your frame: your signature recharges 40% faster.',
    drawback: 'Each signature use costs 8 HP',
    tags: ['electric'],
    stats: [{ stat: 'signatureCooldown', op: 'mul', value: 0.6 }],
    effects: [],
    flags: ['FEEDBACK_LOOP'],
    aura: 0xc78bff,
    weight: 1,
  },
  magnet_heart: {
    id: 'magnet_heart',
    name: 'Magnet Heart',
    short: 'Scrap magnet',
    desc: 'Triple pickup radius and +25% scrap. Overheating opens a 200 px gravity well around you for 1.5 s.',
    drawback: 'The well drags enemies right onto you',
    tags: ['gravity'],
    stats: [
      { stat: 'pickupRadius', op: 'mul', value: 3 },
      { stat: 'scrapGain', op: 'mul', value: 1.25 },
    ],
    effects: [{ trigger: 'onOverheat', action: { type: 'gravity', radius: 200, duration: 1.5, strength: 1300 } }],
    aura: COLORS.gravity,
    weight: 1,
  },
  overcharge: {
    id: 'overcharge',
    name: 'Overcharge',
    short: 'Ultimate engine',
    desc: 'Your ultimate charges twice as fast.',
    drawback: 'Max shield halved',
    tags: [],
    stats: [
      { stat: 'ultimateCharge', op: 'mul', value: 2 },
      { stat: 'maxShield', op: 'mul', value: 0.5 },
    ],
    effects: [],
    aura: COLORS.ultBar,
    weight: 1,
  },
  berserk_servos: {
    id: 'berserk_servos',
    name: 'Berserk Servos',
    short: 'Low-HP fury',
    desc: 'Below 40% HP your servos redline: +35% damage and +35% fire rate.',
    drawback: 'Shields wait 50% longer to recharge',
    tags: ['kinetic'],
    stats: [{ stat: 'shieldDelay', op: 'mul', value: 1.5 }],
    effects: [],
    flags: ['BERSERK'],
    aura: 0xff6a3d,
    weight: 0.9,
    unlock: 'mutation:berserk_servos',
  },
  arc_echo: {
    id: 'arc_echo',
    name: 'Arc Echo',
    short: 'Double discharge',
    desc: 'Arc Dash discharges a second time along its path 0.6 s later.',
    drawback: '+20% signature cooldown',
    tags: ['electric', 'dash'],
    stats: [{ stat: 'signatureCooldown', op: 'mul', value: 1.2 }],
    effects: [],
    mech: 'volt',
    flags: ['ARC_ECHO'],
    aura: COLORS.volt,
    weight: 1.4,
  },
  siege_lock: {
    id: 'siege_lock',
    name: 'Siege Lock',
    short: 'Entrenched field',
    desc: 'Siege Field lasts 50% longer and slows every enemy inside it.',
    drawback: '-10% move speed',
    tags: ['shield'],
    stats: [{ stat: 'moveSpeed', op: 'mul', value: 0.9 }],
    effects: [],
    mech: 'bastion',
    flags: ['SIEGE_LOCK'],
    aura: COLORS.bastion,
    weight: 1.4,
  },
  ghost_protocol: {
    id: 'ghost_protocol',
    name: 'Ghost Protocol',
    short: 'Lingering echoes',
    desc: 'Phase Echo echoes last 2 s longer and fire at 75% power.',
    drawback: '-10% max HP',
    tags: ['precision'],
    stats: [{ stat: 'maxHp', op: 'mul', value: 0.9 }],
    effects: [],
    mech: 'wraith',
    flags: ['GHOST_PROTOCOL'],
    aura: COLORS.wraith,
    weight: 1.4,
  },
};

// ───────────────────────────── Synergies ─────────────────────────────

export const SYNERGIES: Record<SynergyId, SynergyDef> = {
  storm_runner: {
    id: 'storm_runner',
    name: 'Storm Runner',
    desc: 'Arc Dash chains strike twice with +30% range, chain lightning gains a target, and every dash sparks a 2-target chain.',
    requires: { electric: 3, chain: 2 },
    stats: [{ stat: 'chainBonus', op: 'add', value: 1 }],
    effects: [{ trigger: 'onDash', cooldown: 0.3, action: { type: 'chain', count: 2, range: 260, damage: 22 } }],
    flags: ['STORM_RUNNER'],
    color: COLORS.electric,
  },
  walking_fortress: {
    id: 'walking_fortress',
    name: 'Walking Fortress',
    desc: 'Stand still for 0.5 s to brace: +25% damage dealt and -25% damage taken.',
    requires: { shield: 2, precision: 1 },
    flags: ['WALKING_FORTRESS'],
    color: COLORS.bastion,
  },
  gravity_furnace: {
    id: 'gravity_furnace',
    name: 'Gravity Furnace',
    desc: 'Gravity fields set enemies ablaze and overheated enemies explode on death. +15% explosion radius.',
    requires: { gravity: 1, burn: 1, explosive: 1 },
    stats: [{ stat: 'explosionRadius', op: 'mul', value: 1.15 }],
    flags: ['GRAVITY_FURNACE'],
    color: COLORS.gravity,
  },
  conductor: {
    id: 'conductor',
    name: 'Conductor',
    desc: 'Chain lightning jumps to 1 extra target.',
    requires: { electric: 2 },
    stats: [{ stat: 'chainBonus', op: 'add', value: 1 }],
    color: COLORS.chain,
  },
  wildfire: {
    id: 'wildfire',
    name: 'Wildfire',
    desc: 'Burning enemies spread their flames on death. +25% burn damage.',
    requires: { burn: 3 },
    stats: [{ stat: 'burnPower', op: 'mul', value: 1.25 }],
    flags: ['WILDFIRE'],
    color: COLORS.burn,
  },
  demolition: {
    id: 'demolition',
    name: 'Demolition',
    desc: '+35% explosion radius on every blast you cause.',
    requires: { explosive: 3 },
    stats: [{ stat: 'explosionRadius', op: 'mul', value: 1.35 }],
    color: COLORS.explosive,
  },
  bulwark: {
    id: 'bulwark',
    name: 'Bulwark',
    desc: 'Shields start recharging 40% sooner, and a shield break unleashes a 240 px shockwave.',
    requires: { shield: 3 },
    stats: [{ stat: 'shieldDelay', op: 'mul', value: 0.6 }],
    effects: [{ trigger: 'onShieldBreak', action: { type: 'shockwave', radius: 240, damage: 45, knockback: 1100 } }],
    color: COLORS.shield,
  },
  afterburner: {
    id: 'afterburner',
    name: 'Afterburner',
    desc: '-20% dash cooldown, and every dash scorches the ground behind you.',
    requires: { dash: 3 },
    stats: [{ stat: 'dashCooldown', op: 'mul', value: 0.8 }],
    effects: [
      {
        trigger: 'onDashEnd',
        action: { type: 'trail', kind: 'burn', dps: 20, duration: 1.8, status: { id: 'burn', stacks: 1, duration: 3, potency: 5 } },
      },
    ],
    color: COLORS.heat,
  },
};

// ───────────────────────────── Major upgrades (boss rewards) ─────────────────────────────

export const MAJOR_UPGRADES: Record<MajorUpgradeId, UpgradeData> = {
  core_reinforce: {
    id: 'core_reinforce',
    name: 'Core Reinforcement',
    short: 'Sturdier frame',
    desc: '+25 max HP for the rest of the run.',
    stats: [{ stat: 'maxHp', op: 'add', value: 25 }],
  },
  twin_jets: {
    id: 'twin_jets',
    name: 'Twin Jets',
    short: 'Extra air jump',
    desc: '+1 air jump. Stay above the fight.',
    stats: [{ stat: 'airJumps', op: 'add', value: 1 }],
  },
  signature_evolve: {
    id: 'signature_evolve',
    name: 'Signature Evolution',
    short: 'Evolved power',
    desc: 'Your signature evolves. VOLT: Arc Dash leaves a shock trail and chains twice. BASTION: Siege Field damages enemies touching it. WRAITH: echoes last 50% longer.',
    flags: ['SIGNATURE_EVOLVED'],
  },
  ultimate_amp: {
    id: 'ultimate_amp',
    name: 'Ultimate Amplifier',
    short: 'Faster ultimate',
    desc: 'Your ultimate charges 50% faster.',
    stats: [{ stat: 'ultimateCharge', op: 'mul', value: 1.5 }],
  },
  heat_mastery: {
    id: 'heat_mastery',
    name: 'Heat Mastery',
    short: 'Run cooler',
    desc: '+30% heat capacity and +25% cooling on every weapon.',
    stats: [
      { stat: 'heatCapacity', op: 'mul', value: 1.3 },
      { stat: 'cooling', op: 'mul', value: 1.25 },
    ],
  },
  mod_slot: {
    id: 'mod_slot',
    name: 'Auxiliary Mod Rail',
    short: 'More mods',
    desc: 'Every weapon gets +1 mod slot, including weapons you find later.',
    flags: ['MOD_SLOT'],
  },
  shield_matrix: {
    id: 'shield_matrix',
    name: 'Shield Matrix',
    short: 'Stronger shields',
    desc: '+30 max shield and +30% shield regeneration.',
    stats: [
      { stat: 'maxShield', op: 'add', value: 30 },
      { stat: 'shieldRegen', op: 'mul', value: 1.3 },
    ],
  },
  salvage_protocol: {
    id: 'salvage_protocol',
    name: 'Salvage Protocol',
    short: 'Scrap economy',
    desc: '+50% scrap from kills, drops and salvage.',
    stats: [{ stat: 'scrapGain', op: 'mul', value: 1.5 }],
  },
  dash_capacitor: {
    id: 'dash_capacitor',
    name: 'Dash Capacitor',
    short: 'Extra dash',
    desc: '+1 air dash and 25% faster dash recharge.',
    stats: [
      { stat: 'airDashes', op: 'add', value: 1 },
      { stat: 'dashCooldown', op: 'mul', value: 0.75 },
    ],
  },
};

// ───────────────────────────── Behaviour flag text ─────────────────────────────

/** Positive card line per behaviour flag ('' = the flag only carries a drawback). */
export const FLAG_TEXT: Readonly<Record<string, string>> = {
  ARC_ECHO: 'Arc Dash discharges again along its path 0.6 s later',
  SIEGE_LOCK: 'Siege Field lasts 50% longer and slows enemies inside',
  GHOST_PROTOCOL: 'Echoes last 2 s longer and fire at 75% power',
  FEEDBACK_LOOP: '',
  CHAIN_REACTOR: 'Chain-lightning hits apply 1 burn stack',
  MELTDOWN: 'Overheat no longer locks your weapon; overheated shots deal +50%',
  VOLATILE_PAYLOAD: 'Kills explode for 25% of the victim\'s max HP (90 px)',
  PHASE_CAPACITOR: 'Dash i-frames +0.12 s; enemies dashed through take 2 shock stacks',
  BERSERK: 'Below 40% HP: +35% damage and fire rate',
  STORM_RUNNER: 'Arc Dash chains strike twice, +30% chain range',
  WALKING_FORTRESS: 'Brace after 0.5 s still: +25% damage, -25% damage taken',
  GRAVITY_FURNACE: 'Gravity fields ignite; overheated enemies explode on death',
  WILDFIRE: 'Burning enemies spread their flames on death',
  SIGNATURE_EVOLVED: 'Your signature power evolves',
  MOD_SLOT: '+1 mod slot on every weapon',
};

/** What SIGNATURE_EVOLVED does for each mech (card text). */
export const SIGNATURE_EVOLUTION_TEXT: Readonly<Record<MechId, string>> = {
  volt: 'Arc Dash leaves a shock trail and chains twice',
  bastion: 'Siege Field damages every enemy that touches it',
  wraith: 'Phase Echo echoes last 50% longer',
};

// ───────────────────────────── Affix pools ─────────────────────────────

export interface AffixDef {
  id: string;
  stat: StatKey;
  op: 'add' | 'mul';
  /** Advanced-tier value. */
  value: number;
  /** Prototype-tier ("strong") value. */
  strong: number;
  /** Item kinds that can roll it. */
  kinds: readonly ItemKind[];
  /** Item must carry at least one of these tags. */
  tags?: readonly Tag[];
  /** Equipment must sit in one of these slots (ignored for other kinds). */
  slots?: readonly EquipSlot[];
  weight: number;
}

const ALL_KINDS: readonly ItemKind[] = ['weapon', 'equipment', 'mod', 'mutation'];
const GEAR: readonly ItemKind[] = ['weapon', 'mod'];

/** Positive identity affixes (advanced: one; prototype: one at the strong value). No raw damage. */
export const POSITIVE_AFFIXES: readonly AffixDef[] = [
  { id: 'coolant', stat: 'cooling', op: 'mul', value: 1.2, strong: 1.35, kinds: GEAR, weight: 1 },
  { id: 'heat_buffer', stat: 'heatCapacity', op: 'mul', value: 1.15, strong: 1.3, kinds: ['weapon', 'mod', 'equipment'], slots: ['back'], weight: 1 },
  { id: 'arc_relay', stat: 'chainBonus', op: 'add', value: 1, strong: 2, kinds: ALL_KINDS, tags: ['chain'], weight: 1.3 },
  { id: 'conductive', stat: 'chainDamage', op: 'mul', value: 1.2, strong: 1.35, kinds: ALL_KINDS, tags: ['electric'], weight: 0.9 },
  { id: 'wide_blast', stat: 'explosionRadius', op: 'mul', value: 1.2, strong: 1.35, kinds: ALL_KINDS, tags: ['explosive', 'gravity'], weight: 1 },
  { id: 'shaped_charge', stat: 'explosionDamage', op: 'mul', value: 1.15, strong: 1.3, kinds: ALL_KINDS, tags: ['explosive', 'missile'], weight: 0.7 },
  { id: 'thermite', stat: 'burnPower', op: 'mul', value: 1.25, strong: 1.45, kinds: ALL_KINDS, tags: ['burn'], weight: 1.1 },
  { id: 'targeting', stat: 'critChance', op: 'add', value: 0.06, strong: 0.1, kinds: GEAR, tags: ['precision', 'kinetic'], weight: 0.8 },
  { id: 'hollow_point', stat: 'critMult', op: 'add', value: 0.25, strong: 0.45, kinds: ['weapon', 'mod', 'mutation'], tags: ['precision'], weight: 0.8 },
  { id: 'impact', stat: 'knockback', op: 'mul', value: 1.25, strong: 1.45, kinds: GEAR, tags: ['kinetic'], weight: 0.6 },
  { id: 'quick_cycle', stat: 'fireRate', op: 'mul', value: 1.1, strong: 1.18, kinds: GEAR, weight: 0.6 },
  { id: 'thrusters', stat: 'dashCooldown', op: 'mul', value: 0.85, strong: 0.75, kinds: ['equipment', 'mutation'], tags: ['dash'], weight: 1 },
  { id: 'long_stride', stat: 'dashPower', op: 'mul', value: 1.12, strong: 1.22, kinds: ['equipment', 'mutation'], tags: ['dash'], weight: 0.6 },
  { id: 'servo_tune', stat: 'moveSpeed', op: 'mul', value: 1.06, strong: 1.1, kinds: ['equipment'], slots: ['legs', 'back'], weight: 0.8 },
  { id: 'spring_coils', stat: 'jumpPower', op: 'mul', value: 1.06, strong: 1.1, kinds: ['equipment'], slots: ['legs', 'back'], weight: 0.5 },
  { id: 'capacitor_bank', stat: 'maxShield', op: 'add', value: 15, strong: 25, kinds: ['equipment'], slots: ['chest', 'shoulders'], weight: 1 },
  { id: 'reinforced', stat: 'maxHp', op: 'add', value: 15, strong: 25, kinds: ['equipment'], slots: ['chest', 'legs'], weight: 1 },
  { id: 'fast_charge', stat: 'shieldRegen', op: 'mul', value: 1.25, strong: 1.4, kinds: ['equipment', 'mutation'], tags: ['shield'], weight: 0.8 },
  { id: 'hair_trigger', stat: 'shieldDelay', op: 'mul', value: 0.85, strong: 0.72, kinds: ['equipment', 'mutation'], tags: ['shield'], weight: 0.6 },
  { id: 'plating', stat: 'armor', op: 'add', value: 0.05, strong: 0.08, kinds: ['equipment'], slots: ['chest', 'legs'], weight: 0.7 },
  { id: 'stabilizers', stat: 'knockbackResist', op: 'add', value: 0.2, strong: 0.35, kinds: ['equipment'], slots: ['chest', 'legs'], weight: 0.4 },
  { id: 'focus_lens', stat: 'signatureCooldown', op: 'mul', value: 0.88, strong: 0.8, kinds: ['equipment', 'mutation'], weight: 0.8 },
  { id: 'surge_cells', stat: 'ultimateCharge', op: 'mul', value: 1.15, strong: 1.3, kinds: ['weapon', 'equipment', 'mutation'], weight: 0.8 },
  { id: 'tractor', stat: 'pickupRadius', op: 'mul', value: 1.5, strong: 2, kinds: ['equipment', 'mutation'], weight: 0.4 },
  { id: 'reclaimer', stat: 'scrapGain', op: 'mul', value: 1.12, strong: 1.22, kinds: ALL_KINDS, weight: 0.4 },
];

/** Prototype drawbacks (one per prototype item; `value` is used, `strong` mirrors it). */
export const NEGATIVE_AFFIXES: readonly AffixDef[] = [
  { id: 'brittle', stat: 'maxHp', op: 'add', value: -10, strong: -10, kinds: ALL_KINDS, weight: 1 },
  { id: 'leaky_cells', stat: 'maxShield', op: 'add', value: -12, strong: -12, kinds: ALL_KINDS, weight: 1 },
  { id: 'exposed', stat: 'damageTaken', op: 'mul', value: 1.08, strong: 1.08, kinds: ALL_KINDS, weight: 0.8 },
  { id: 'heavy', stat: 'moveSpeed', op: 'mul', value: 0.94, strong: 0.94, kinds: ALL_KINDS, weight: 0.8 },
  { id: 'runs_hot', stat: 'heatCapacity', op: 'mul', value: 0.88, strong: 0.88, kinds: GEAR, weight: 1 },
  { id: 'clogged', stat: 'cooling', op: 'mul', value: 0.85, strong: 0.85, kinds: ['weapon', 'mod', 'equipment'], weight: 0.8 },
  { id: 'sluggish_jets', stat: 'dashCooldown', op: 'mul', value: 1.15, strong: 1.15, kinds: ['equipment', 'mutation'], weight: 0.8 },
  { id: 'feedback', stat: 'signatureCooldown', op: 'mul', value: 1.12, strong: 1.12, kinds: ['equipment', 'mutation'], weight: 0.7 },
  { id: 'slow_recovery', stat: 'shieldDelay', op: 'mul', value: 1.2, strong: 1.2, kinds: ['equipment', 'mutation'], weight: 0.7 },
  { id: 'jammed', stat: 'fireRate', op: 'mul', value: 0.92, strong: 0.92, kinds: GEAR, weight: 0.8 },
];

// ───────────────────────────── Lookup helpers ─────────────────────────────

export const WEAPON_DEF_IDS = Object.keys(WEAPONS) as WeaponId[];
export const MOD_DEF_IDS = Object.keys(MODS) as ModId[];
export const EQUIP_DEF_IDS = Object.keys(EQUIPMENT) as EquipId[];
export const MUTATION_DEF_IDS = Object.keys(MUTATIONS) as MutationId[];
export const UPGRADE_DEF_IDS = Object.keys(MAJOR_UPGRADES) as MajorUpgradeId[];
export const SYNERGY_DEF_IDS = Object.keys(SYNERGIES) as SynergyId[];
